import { useEffect, useRef, useState, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { Journey } from '../data/projectTypes'
import ModalPortal from './ModalPortal'
import OverviewJourneyPlayer from './OverviewJourneyPlayer'
import './JourneyLightbox.css'

// Distance (px) a horizontal touch swipe must travel — and by which it must dominate any vertical
// movement — to count as a prev/next gesture rather than a tap or a vertical scroll of the bug list.
const SWIPE_THRESHOLD = 50

// A journey that is sliding off-screen while the newly selected one slides in. `dir` is the
// navigation direction: 'next' moves forward (outgoing exits left, incoming enters from the right),
// 'prev' moves back (the mirror image).
type Slide = { dir: 'next' | 'prev'; outgoing: Journey; outgoingIndex: number }

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

// The journeys gallery on the (experimental) project overview opens the selected journey in this
// lightbox modal instead of expanding the card in place. Expanding in place reflowed the grid and
// shifted the page's scroll position out from under the user; a modal overlay leaves the underlying
// page untouched. Navigation controls (and left/right swipe on touch devices) cycle through the
// gallery without dismissing, and the overlay / close button / Escape all dismiss back to the grid.
export default function JourneyLightbox({ journeys, index, projectId, onClose, onNavigate }: {
  journeys: Journey[]
  index: number
  projectId: string
  onClose: () => void
  onNavigate: (index: number) => void
}) {
  const journey = journeys[index]
  const hasPrev = index > 0
  const hasNext = index < journeys.length - 1

  const goPrev = useCallback(() => { if (index > 0) onNavigate(index - 1) }, [index, onNavigate])
  const goNext = useCallback(() => { if (index < journeys.length - 1) onNavigate(index + 1) }, [index, journeys.length, onNavigate])

  // Cross-slide the outgoing journey out while the incoming one slides in. When `index` changes we
  // remember the journey we're leaving and which way we're going; the track then animates and clears
  // the slide on animationend, dropping the outgoing journey (and its replay) from the DOM. Rapid
  // navigation re-keys the track so the new slide restarts cleanly. Respects reduced-motion.
  const [slide, setSlide] = useState<Slide | null>(null)
  const prevIndexRef = useRef(index)
  useEffect(() => {
    const prev = prevIndexRef.current
    prevIndexRef.current = index
    if (index === prev) return
    const outgoing = journeys[prev]
    if (!outgoing || prefersReducedMotion()) { setSlide(null); return }
    setSlide({ dir: index > prev ? 'next' : 'prev', outgoing, outgoingIndex: prev })
  }, [index, journeys])

  // Keyboard: Escape dismisses; arrow keys move between journeys (desktop/tablet navigation).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, goPrev, goNext])

  // Lock body scroll while the lightbox is open so the page behind can't scroll under the overlay.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Touch swipe (mobile): remember where a touch started, and on release decide whether it was a
  // mostly-horizontal swipe past the threshold. Vertical-dominant moves fall through so the bug list
  // still scrolls normally.
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.changedTouches[0]
    if (t) touchStart.current = { x: t.clientX, y: t.clientY }
  }, [])
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const start = touchStart.current
    touchStart.current = null
    const t = e.changedTouches[0]
    if (!start || !t) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return
    if (dx < 0) goNext() // swipe left → next
    else goPrev() // swipe right → previous
  }, [goPrev, goNext])

  if (!journey) return null

  // One journey's stage content (replay player + position counter). Rendered once when idle and,
  // during a transition, once per layer so the outgoing and incoming journeys each show correctly.
  const renderStage = (j: Journey, pos: number) => (
    <div className="journey-lightbox__stage" data-testid={`journey-lightbox-stage-${j.id}`}>
      {/* key on the journey id so switching journeys remounts the player (fresh replay + bug fetch). */}
      <OverviewJourneyPlayer
        key={j.id}
        journey={j}
        projectId={projectId}
        onClose={onClose}
      />
      {journeys.length > 1 && (
        <div className="journey-lightbox__counter" data-testid="journey-lightbox-counter">
          {pos + 1} / {journeys.length}
        </div>
      )}
    </div>
  )

  // Order the track's layers so the animation direction reads right: for 'next' the outgoing journey
  // sits left of the incoming one and the track slides left; for 'prev' the incoming sits left and
  // the track slides back to rest. Each layer is keyed by journey id so the surviving (current) layer
  // is preserved — not remounted — when the outgoing layer is dropped on animationend.
  const layers = !slide
    ? [{ j: journey, pos: index }]
    : slide.dir === 'next'
      ? [{ j: slide.outgoing, pos: slide.outgoingIndex }, { j: journey, pos: index }]
      : [{ j: journey, pos: index }, { j: slide.outgoing, pos: slide.outgoingIndex }]

  return (
    <ModalPortal>
      <div
        className="journey-lightbox__overlay"
        onClick={onClose}
        data-testid="journey-lightbox"
        data-analytics-event="new-overview-journey-lightbox-overlay-close"
      >
        <button
          type="button"
          className="journey-lightbox__nav journey-lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); goPrev() }}
          disabled={!hasPrev}
          aria-label="Previous journey"
          data-testid="journey-lightbox-prev"
          data-analytics-event="new-overview-journey-lightbox-prev"
        >
          <ChevronLeft size={24} aria-hidden />
        </button>

        <div
          className="journey-lightbox__viewport"
          role="dialog"
          aria-modal="true"
          aria-label={`Journey: ${journey.name}`}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Re-key the track per current journey so a fresh navigation restarts the slide from frame
              zero even if the previous one hasn't settled. */}
          <div
            key={journey.id}
            className={`journey-lightbox__track${slide ? ` journey-lightbox__track--${slide.dir}` : ''}`}
            onAnimationEnd={(e) => { if (e.target === e.currentTarget) setSlide(null) }}
          >
            {layers.map(({ j, pos }) => (
              <div className="journey-lightbox__layer" key={j.id} aria-hidden={slide ? j.id !== journey.id : undefined}>
                {renderStage(j, pos)}
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="journey-lightbox__nav journey-lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); goNext() }}
          disabled={!hasNext}
          aria-label="Next journey"
          data-testid="journey-lightbox-next"
          data-analytics-event="new-overview-journey-lightbox-next"
        >
          <ChevronRight size={24} aria-hidden />
        </button>
      </div>
    </ModalPortal>
  )
}
